import React from 'react';
import { SEO } from '@/components/SEO';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceSchema from '@/components/services/ServiceSchema';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import LazyImage from '@/components/ui/LazyImage';
import { Sparkles } from 'lucide-react';

const WindowWashingPage = () => {
  const serviceData = {
    title: "Professional Window Cleaning in Ventura, CA",
    subtitle: "Crystal Clear Views for Your Ventura County Property",
    description: "Achieve streak-free, sparkling windows that enhance your property's curb appeal and let in maximum natural light. Our professional window cleaning services use advanced techniques perfect for Ventura's coastal conditions.",
    icon: <Sparkles className="h-16 w-16 text-ventura-blue" />,
    beforeImage: "/lovable-uploads/50da41c5-f891-4836-974c-772b27c05f1a.webp",
    afterImage: "/lovable-uploads/862089e8-1263-474b-8de2-ba77e319b7af.webp"
  };

  const benefits = [
    "Streak-free, crystal clear results",
    "Enhanced natural light and views",
    "Professional-grade equipment and solutions",
    "Interior and exterior cleaning available",
    "Safe cleaning for all window types",
    "Regular maintenance programs available"
  ];

  const faqs = [
    {
      question: "How often should I have my windows cleaned in Ventura?",
      answer: "Due to Ventura's coastal location, we recommend professional window cleaning every 2-3 months to combat salt air buildup and maintain crystal clear views."
    },
    {
      question: "Do you clean both interior and exterior windows?",
      answer: "Yes! We provide comprehensive window cleaning services for both interior and exterior surfaces, ensuring your Ventura property looks pristine inside and out."
    },
    {
      question: "What areas of Ventura County do you serve?",
      answer: "We provide window cleaning services throughout Ventura County, including Camarillo, Fillmore, Moorpark, Oxnard, Port Hueneme, Santa Paula, Simi Valley, Thousand Oaks, and Ventura."
    }
  ];

  return (
    <>
      <SEO
        title="Window Cleaning Ventura CA | Professional Window Washing Services"
        description="Expert window cleaning Ventura & Oxnard CA. Streak-free glass for homes & businesses in Thousand Oaks, Camarillo. Eco-friendly methods. Free quotes!"
        keywords="window cleaning Ventura CA, window washing Camarillo, glass cleaning Thousand Oaks, Ventura County window services"
        path="/services/window-washing"
      />
      <ServiceSchema
        serviceName="Window Cleaning"
        description="Expert window cleaning Ventura & Oxnard CA. Streak-free glass for homes & businesses in Thousand Oaks, Camarillo."
        serviceType="Window Cleaning"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Solar Panel Cleaning", "Pressure Washing", "Soft Wash"]}
      />
      
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />
        
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">How Often Should You Clean Your Windows in Ventura County's Coastal Climate?</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Due to Ventura's coastal location, we recommend professional window cleaning every 2-3 months to combat salt air buildup —
                more frequently than inland areas need, since marine layer moisture leaves mineral deposits and haze on glass faster than
                dust alone. Our professional cleaning restores clarity and protects your investment in quality windows.
              </p>
              
              {/* Window Cleaning Section */}
              <div className="max-w-4xl mx-auto mb-12">
                <h3 className="text-3xl font-bold mb-6 text-center">Window Cleaning Camarillo</h3>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-gray-600 mb-4">
                      Enjoy crystal-clear views of Camarillo's beautiful landscape with our professional window cleaning services. Our streak-free technique and purified water system ensure spotless results on both residential and commercial properties throughout Camarillo and surrounding areas.
                    </p>
                    <p className="text-gray-600 mb-4">
                      We understand Camarillo's unique climate challenges, from seasonal Santa Ana winds that bring dust to morning marine layer moisture. Our comprehensive service includes interior and exterior cleaning, screen washing, and sill detailing to maintain pristine windows year-round.
                    </p>
                    <div className="bg-primary/10 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-primary mb-2">Professional Window Cleaning Starting at $150</h4>
                      <p className="text-sm text-gray-600">Includes interior/exterior cleaning and screen washing</p>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">FAQ: How often should I clean windows in Camarillo?</h4>
                      <p className="text-gray-600 text-sm">Due to Camarillo's dust and seasonal winds, we recommend quarterly professional cleaning for optimal clarity and longevity of your windows.</p>
                    </div>
                    <Link to="/#contact" className="text-primary font-semibold hover:underline">
                      Book Your Camarillo Window Cleaning →
                    </Link>
                  </div>
                  <div className="before-after-container">
                    <div className="before-after-image">
                      <LazyImage 
                        src="/lovable-uploads/50da41c5-f891-4836-974c-772b27c05f1a.webp" 
                        alt="Before window cleaning Camarillo - dirty streaked windows with water spots" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label before-label">Before</span>
                    </div>
                    <div className="before-after-image">
                      <LazyImage 
                        src="/lovable-uploads/862089e8-1263-474b-8de2-ba77e319b7af.webp" 
                        alt="After window cleaning Camarillo - crystal clear spotless windows" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label after-label">After</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Complete Window Care Services</h3>
                  <p className="text-gray-600 mb-4">
                    From residential homes to commercial buildings across Ventura County:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Residential window cleaning</li>
                    <li>Commercial storefront cleaning</li>
                    <li>High-rise window services</li>
                    <li>Screen cleaning and repair</li>
                    <li>Window frame and sill cleaning</li>
                    <li>Post-construction cleanup</li>
                  </ul>
                </div>
                <div className="before-after-container">
                  <div className="before-after-image">
                    <img 
                      src={serviceData.beforeImage} 
                      alt="Dirty windows before professional cleaning in Ventura CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label before-label">Before</span>
                  </div>
                  <div className="before-after-image">
                    <img 
                      src={serviceData.afterImage} 
                      alt="Crystal clear windows after professional cleaning" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label after-label">After</span>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Professional Window Cleaning Techniques</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Traditional Squeegee Method</h4>
                    <p className="text-gray-600 mb-4">
                      Our experienced technicians use professional-grade squeegees and premium cleaning solutions to achieve streak-free results on windows throughout Camarillo, Oxnard, and Thousand Oaks. This time-tested method ensures optimal clarity for residential and commercial properties.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Pure Water Technology</h4>
                    <p className="text-gray-600 mb-4">
                      For multi-story buildings and hard-to-reach windows in Ventura County, we employ pure water fed-pole systems. This innovative approach uses deionized water that leaves no mineral deposits, perfect for maintaining crystal-clear windows in our mineral-rich coastal environment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Addressing Ventura County's Unique Window Challenges</h3>
                <p className="text-gray-600 mb-4">
                  Living near the Pacific Ocean means your windows face constant exposure to salt air, particularly in coastal communities like Port Hueneme and Ventura. This salt buildup not only obscures your view but can also cause permanent etching if not properly addressed with regular professional cleaning.
                </p>
                <p className="text-gray-600 mb-4">
                  Inland areas like Fillmore and Santa Paula present different challenges, with agricultural dust and seasonal pollen creating stubborn films on glass surfaces. Our specialized cleaning solutions are formulated to handle these specific contaminants while being safe for your property's landscaping and exterior finishes.
                </p>
                <p className="text-gray-600 mb-6">
                  We also understand the impact of Ventura County's famous winds, which can quickly redeposit dirt and debris on freshly cleaned windows. That's why we offer flexible maintenance schedules designed to keep your windows pristine year-round, adapting our service frequency to match seasonal conditions and your property's specific exposure.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-12">
                <h3 className="text-2xl font-semibold mb-4">Commercial Window Cleaning Excellence</h3>
                <p className="text-gray-600 mb-4">
                  From small storefronts in downtown Ventura to large office complexes in Thousand Oaks, we provide comprehensive commercial window cleaning services that enhance your business's professional appearance. Clean windows create positive first impressions and can even improve employee productivity by maximizing natural light.
                </p>
                <p className="text-gray-600">
                  Our commercial services include flexible scheduling to minimize disruption to your business operations, comprehensive insurance coverage, and detailed cleaning protocols that meet the highest industry standards. We work with property managers, business owners, and facility maintenance teams throughout Ventura County to deliver consistent, reliable results.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Safety and Environmental Responsibility</h3>
                <p className="text-gray-600 mb-4">
                  Safety is our top priority in every window cleaning project. Our team is fully trained in proper ladder safety, high-reach equipment operation, and fall protection protocols. We carry comprehensive insurance and follow all OSHA guidelines to protect both our technicians and your property.
                </p>
                <p className="text-gray-600">
                  We're also committed to environmental stewardship in beautiful Ventura County. Our cleaning solutions are biodegradable and safe for surrounding vegetation, and we follow water conservation practices that respect California's environmental priorities while delivering exceptional cleaning results.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks 
          currentService="Window Cleaning"
          relatedServices={[
            { name: 'Solar Panel Cleaning', path: '/services/solar-panel-cleaning', icon: 'solar' },
            { name: 'Pressure Washing', path: '/services/pressure-washing', icon: 'pressure' },
            { name: 'Soft Wash', path: '/services/soft-wash', icon: 'soft' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Window Cleaning" />
        <ServiceCTA 
          title="Ready for Crystal Clear Windows?"
          description="Schedule your professional window cleaning service and enjoy pristine views throughout your Ventura County property."
        />
        <Footer />
      </div>
    </>
  );
};

export default WindowWashingPage;